// ============================================
// Alert Service - Real-time bid monitoring
// ============================================

import { AlertEvent, AlertType, Bid, DomainItem, NotificationSettings } from "../types";

// Extended Bid info with endTime (from cache or auction lookup)
// Note: BidWithEndTime interface reserved for future auction end time tracking
import { DropCatchService } from "./dropcatchService";
import { ResendService } from "./resendService";
import { storageService } from "./storageService";

// ============================================
// Types
// ============================================

interface BidStatus {
  auctionId: number;
  domain: string;
  highBid: number;
  isWinning: boolean;
  endTime: string;
  lastChecked: Date;
}

interface AlertCallback {
  (alert: AlertEvent): void;
}

// ============================================
// Alert Service Class
// ============================================

export class AlertService {
  private pollInterval: number = 30000; // 30 seconds for bids
  private snipeCheckInterval: number = 10000; // 10 seconds for snipe warnings
  private bidCache: Map<number, BidStatus> = new Map();
  private intervalId: NodeJS.Timeout | null = null;
  private snipeIntervalId: NodeJS.Timeout | null = null;
  private currentUser: string | null = null;
  private callbacks: Set<AlertCallback> = new Set();
  private dropcatchService: DropCatchService | null = null;
  private resendService: ResendService | null = null;
  private notificationSettings: NotificationSettings | null = null;

  // ==========================================
  // Initialization
  // ==========================================

  /**
   * Initialize alert service with services
   */
  initialize(
    dropcatchService: DropCatchService,
    resendService: ResendService
  ): void {
    this.dropcatchService = dropcatchService;
    this.resendService = resendService;
  }

  /**
   * Start monitoring for a user
   */
  startMonitoring(username: string): void {
    if (this.intervalId) {
      this.stopMonitoring();
    }

    this.currentUser = username;

    // Load notification settings
    const prefs = storageService.getUserPreferences(username);
    this.notificationSettings = prefs.notifications;

    // Initial bid check
    this.checkBidStatus();

    // Start polling intervals
    this.intervalId = setInterval(() => {
      this.checkBidStatus();
    }, this.pollInterval);

    // Start snipe warning checks (more frequent)
    this.snipeIntervalId = setInterval(() => {
      this.checkSnipeWarnings();
    }, this.snipeCheckInterval);

    console.log(`[AlertService] Started monitoring for ${username}`);
  }

  /**
   * Stop monitoring
   */
  stopMonitoring(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.snipeIntervalId) {
      clearInterval(this.snipeIntervalId);
      this.snipeIntervalId = null;
    }
    this.currentUser = null;
    this.bidCache.clear();
    console.log("[AlertService] Stopped monitoring");
  }

  /**
   * Subscribe to alerts
   */
  subscribe(callback: AlertCallback): () => void {
    this.callbacks.add(callback);
    return () => this.callbacks.delete(callback);
  }

  /**
   * Update notification settings
   */
  updateSettings(settings: NotificationSettings): void {
    this.notificationSettings = settings;
  }

  // ==========================================
  // Bid Status Checking
  // ==========================================

  /**
   * Check current bid status and detect changes
   */
  private async checkBidStatus(): Promise<void> {
    if (!this.dropcatchService || !this.currentUser) return;

    try {
      const response = await this.dropcatchService.getBids({ size: 100 });
      const currentBids = response.items;

      for (const bid of currentBids) {
        const cached = this.bidCache.get(bid.auctionId);

        if (cached) {
          // Check for outbid
          if (cached.isWinning && !bid.isWinning) {
            await this.createOutbidAlert(bid, cached);
          }
          // Check for won auction (time expired and still winning)
          if (bid.isWinning && cached.endTime && new Date(cached.endTime) < new Date()) {
            await this.createWonAlert(bid, cached);
          }
        }

        // Update cache - preserve endTime from previous cache if available
        const previousEndTime = cached?.endTime || "";
        this.bidCache.set(bid.auctionId, {
          auctionId: bid.auctionId,
          domain: bid.domain || "",
          highBid: bid.highBid,
          isWinning: bid.isWinning,
          endTime: previousEndTime,
          lastChecked: new Date(),
        });
      }

      // Check for lost auctions (in cache but not in current bids and expired)
      for (const [auctionId, cached] of this.bidCache) {
        const stillActive = currentBids.find((b) => b.auctionId === auctionId);
        if (!stillActive && cached.endTime && new Date(cached.endTime) < new Date() && !cached.isWinning) {
          await this.createLostAlert(cached);
          this.bidCache.delete(auctionId);
        }
      }
    } catch (error) {
      console.error("[AlertService] Error checking bid status:", error);
    }
  }

  /**
   * Check for auctions ending soon (snipe warnings)
   */
  private async checkSnipeWarnings(): Promise<void> {
    if (!this.currentUser || !this.notificationSettings) return;

    const snipeMinutes = this.notificationSettings.snipeAlertMinutes || 5;
    const now = new Date();

    for (const [auctionId, cached] of this.bidCache) {
      const endTime = new Date(cached.endTime);
      const minutesRemaining = (endTime.getTime() - now.getTime()) / 60000;

      // Check if within snipe window and haven't already alerted
      if (minutesRemaining > 0 && minutesRemaining <= snipeMinutes) {
        // Check if we already sent a snipe alert for this auction
        const alerts = storageService.getUserAlerts(this.currentUser);
        const alreadyAlerted = alerts.some(
          (a) =>
            a.type === "snipe_warning" &&
            a.auctionId === auctionId &&
            new Date(a.timestamp).getTime() > now.getTime() - snipeMinutes * 60000
        );

        if (!alreadyAlerted) {
          await this.createSnipeAlert(cached, Math.floor(minutesRemaining));
        }
      }
    }
  }

  // ==========================================
  // Alert Creation
  // ==========================================

  /**
   * Create outbid alert
   */
  private async createOutbidAlert(bid: Bid, cached: BidStatus): Promise<void> {
    const alert = this.createAlert("outbid", bid.domain || "", bid.auctionId, {
      previousBid: cached.highBid,
      currentBid: bid.highBid,
      endTime: cached.endTime,
    });

    await this.processAlert(alert);
  }

  /**
   * Create won auction alert
   */
  private async createWonAlert(bid: Bid, cached: BidStatus): Promise<void> {
    const alert = this.createAlert("won", bid.domain || "", bid.auctionId, {
      finalPrice: bid.highBid,
      endTime: cached.endTime,
    });

    await this.processAlert(alert);
  }

  /**
   * Create lost auction alert
   */
  private async createLostAlert(cached: BidStatus): Promise<void> {
    const alert = this.createAlert("lost", cached.domain, cached.auctionId, {
      finalPrice: cached.highBid,
      endTime: cached.endTime,
    });

    await this.processAlert(alert);
  }

  /**
   * Create snipe warning alert
   */
  private async createSnipeAlert(
    cached: BidStatus,
    minutesRemaining: number
  ): Promise<void> {
    const alert = this.createAlert("snipe_warning", cached.domain, cached.auctionId, {
      minutesRemaining,
      currentBid: cached.highBid,
      isWinning: cached.isWinning,
      endTime: cached.endTime,
    });

    await this.processAlert(alert);
  }

  /**
   * Create watchlist match alert
   */
  async createWatchlistMatchAlert(
    domains: DomainItem[],
    keyword: string
  ): Promise<void> {
    if (!this.currentUser) return;

    const alert = this.createAlert(
      "watchlist_match",
      domains.map((d) => d.name).join(", "),
      0,
      {
        keyword,
        matchCount: domains.length,
        domains: domains.map((d) => ({
          name: d.name,
          price: d.price,
          endTime: d.endTime,
        })),
      }
    );

    await this.processAlert(alert);
  }

  // ==========================================
  // Alert Processing
  // ==========================================

  /**
   * Create alert object
   */
  private createAlert(
    type: AlertType,
    domain: string,
    auctionId: number,
    data: Record<string, unknown>
  ): AlertEvent {
    return {
      id: `${type}_${auctionId}_${Date.now()}`,
      type,
      domain,
      auctionId,
      timestamp: new Date().toISOString(),
      data,
      read: false,
      emailSent: false,
    };
  }

  /**
   * Process alert (notify callbacks, save, send email)
   */
  private async processAlert(alert: AlertEvent): Promise<void> {
    if (!this.currentUser) return;

    // Notify all subscribers
    this.callbacks.forEach((cb) => cb(alert));

    // Save to storage
    storageService.addAlert(this.currentUser, alert);

    // Send email if enabled
    if (this.shouldSendEmail(alert)) {
      await this.sendEmailAlert(alert);
      alert.emailSent = true;
      // Update alert in storage
      const alerts = storageService.getUserAlerts(this.currentUser);
      const index = alerts.findIndex((a) => a.id === alert.id);
      if (index !== -1) {
        alerts[index] = alert;
        storageService.saveUserAlerts(this.currentUser, alerts);
      }
    }

    console.log(`[AlertService] Alert created: ${alert.type} - ${alert.domain}`);
  }

  /**
   * Check if email should be sent for this alert
   */
  private shouldSendEmail(alert: AlertEvent): boolean {
    if (!this.notificationSettings?.emailEnabled) return false;
    if (!this.notificationSettings.email) return false;

    switch (alert.type) {
      case "outbid":
        return this.notificationSettings.outbidInstantAlert;
      case "snipe_warning":
        return true; // Always send snipe warnings if email enabled
      case "watchlist_match":
        return this.notificationSettings.watchlistMatches;
      case "won":
      case "lost":
        return true; // Always notify for won/lost
      default:
        return false;
    }
  }

  /**
   * Send email alert via Resend
   */
  private async sendEmailAlert(alert: AlertEvent): Promise<void> {
    if (!this.resendService || !this.notificationSettings?.email) return;

    try {
      switch (alert.type) {
        case "outbid":
          await this.resendService.sendOutbidAlert(
            this.notificationSettings.email,
            alert.domain,
            (alert.data.currentBid as number) || 0,
            (alert.data.previousBid as number) || 0
          );
          break;

        case "snipe_warning":
          await this.resendService.sendSnipeAlert(
            this.notificationSettings.email,
            alert.domain,
            (alert.data.minutesRemaining as number) || 5
          );
          break;

        case "won":
          await this.resendService.sendBackorderWonAlert(
            this.notificationSettings.email,
            alert.domain,
            (alert.data.finalPrice as number) || 0
          );
          break;

        case "watchlist_match":
          await this.resendService.sendWatchlistMatchAlert(
            this.notificationSettings.email,
            alert.data.keyword as string,
            alert.data.domains as Array<{ name: string; price: number; endTime: string }>
          );
          break;
      }
    } catch (error) {
      console.error("[AlertService] Failed to send email:", error);
    }
  }

  // ==========================================
  // Manual Alert Methods
  // ==========================================

  /**
   * Manually trigger a test alert
   */
  async sendTestAlert(): Promise<void> {
    if (!this.currentUser) return;

    const alert = this.createAlert("snipe_warning", "test-domain.com", 12345, {
      minutesRemaining: 5,
      currentBid: 100,
      isWinning: true,
      endTime: new Date(Date.now() + 5 * 60000).toISOString(),
    });

    await this.processAlert(alert);
  }

  /**
   * Get all alerts for current user
   */
  getAlerts(): AlertEvent[] {
    if (!this.currentUser) return [];
    return storageService.getUserAlerts(this.currentUser);
  }

  /**
   * Get unread count
   */
  getUnreadCount(): number {
    if (!this.currentUser) return 0;
    return storageService.getUnreadAlertCount(this.currentUser);
  }

  /**
   * Mark alert as read
   */
  markAsRead(alertId: string): void {
    if (!this.currentUser) return;
    storageService.markAlertAsRead(this.currentUser, alertId);
  }

  /**
   * Mark all as read
   */
  markAllAsRead(): void {
    if (!this.currentUser) return;
    storageService.markAllAlertsAsRead(this.currentUser);
  }

  /**
   * Clear all alerts
   */
  clearAlerts(): void {
    if (!this.currentUser) return;
    storageService.clearAlerts(this.currentUser);
  }
}

// Export singleton instance
export const alertService = new AlertService();
